
import { Component, Prop, Watch, Mixins } from 'vue-property-decorator';
import { BAIconsMixin, VuetifyMixin } from '@/mixins';
import { CalendarEventModel } from '@/models/calendar/CalendarEventModel';
import { CalendarEventsApi } from '@/api/CalendarApi';
import { CalendarEventParticipant } from '@/../types/interfaces';
import { CalendarEventParticipantResponse, TeamEventType } from '@/../types/enums';
import { TeamModel } from '@/models/team';
import { UserProfileModel } from '@/models/UserProfileModel';
import { getDefaultEventTypes, getEventColor } from '@/helpers/default-event-types';
import { participantNoteOn, participantRsvpOn, rsvpOccurrenceDate, occurrenceKey } from '@/helpers/availability';
import EventTypesGroup from '@/components/calendar/EventTypesGroup.vue';

/** One event on one date: a repeating event contributes one column per date it falls on. */
interface AvailabilityColumn{
	key: string;
	event: CalendarEventModel;
	date: Date;
	dateLabel: string;
	/** YYYY-MM-DD when the answer belongs to this date alone, undefined for a one-off event. */
	occurrenceDate: string | undefined;
	isPast: boolean;
}

/** One person, matched across events by their athlete or coach id rather than participant id. */
interface AvailabilityRow{
	key: string;
	name: string;
	/** Null for anyone not linked to an athlete or coach profile, which suppresses the lookup. */
	profileId: string | null;
	isStaff: boolean;
	userInfo: Partial<UserProfileModel>;
}

const COLUMNS_PER_PAGE = 12;

@Component({
	components: { EventTypesGroup },
})
export default class AvailabilityGrid extends Mixins(BAIconsMixin, VuetifyMixin){
	/** Events already expanded into one entry per date by the calendar provider. */
	@Prop({ default: () => [] }) occurrences: CalendarEventModel[];
	@Prop({ default: null }) team: TeamModel | null;
	@Prop({ required: true }) calendarEventsApi: CalendarEventsApi;
	@Prop({ default: false, type: Boolean }) canEdit: boolean;
	@Prop({ default: false, type: Boolean }) loading: boolean;
	@Prop({ default: 30 }) range: number;

	page: number = 0;
	savingKey: string | null = null;
	remindingKey: string | null = null;
	error: string = '';
	eventFilters: TeamEventType[] = getDefaultEventTypes().map(type => type.name);

	/**
	 * Answers changed in this session, keyed by cell. The grid holds them itself so a coach can
	 * work down a column without waiting for a reload between clicks.
	 */
	edits: Record<string, CalendarEventParticipantResponse> = {};

	get RangeOptions(): { label: string; value: number }[]{
		return [
			{ label: 'Next 14 days', value: 14 },
			{ label: 'Next 30 days', value: 30 },
			{ label: 'Next 90 days', value: 90 },
			{ label: 'Past 30 days', value: -30 },
		];
	}

	get Legend(): { value: string; label: string; icon: string; color: string }[]{
		return [
			{ value: 'Yes', label: 'Going', icon: this.BAIcons.checkmark, color: 'green' },
			{ value: 'Maybe', label: 'Maybe', icon: this.BAIcons.checkMaybe, color: 'amber darken-2' },
			{ value: 'No', label: 'Not going', icon: this.BAIcons.close, color: 'red' },
			{ value: 'None', label: 'No reply', icon: 'mdi-minus', color: 'grey' },
		];
	}

	get IsMobile(): boolean{
		return this.$vuetify.breakpoint.smAndDown;
	}

	onFilterChange(filters: TeamEventType[]): void{
		this.eventFilters = filters;
		this.page = 0;
	}

	/** One column per event date, oldest first, filtered by the event type chips. */
	get Columns(): AvailabilityColumn[]{
		return this.occurrences
			.filter(event => this.eventFilters.length === 0 || this.eventFilters.includes(event.eventType))
			.slice()
			.sort((a, b) => a.start.getTime() - b.start.getTime())
			.map(event => ({
				key: `${event.id}|${occurrenceKey(event.start)}`,
				event,
				date: event.start,
				dateLabel: this.formatDate(event.start),
				occurrenceDate: rsvpOccurrenceDate(event, event.start),
				isPast: event.start.getTime() < Date.now(),
			}));
	}

	get PageCount(): number{
		return Math.max(1, Math.ceil(this.Columns.length / COLUMNS_PER_PAGE));
	}
	get PagedColumns(): AvailabilityColumn[]{
		const start = this.page * COLUMNS_PER_PAGE;
		return this.Columns.slice(start, start + COLUMNS_PER_PAGE);
	}
	@Watch('Columns')
	onColumnsChanged(): void{
		if(this.page >= this.PageCount) this.page = 0;
	}

	/**
	 * Everyone who appears on any event in range. A person keeps one row across every column,
	 * matched by athlete/coach id, because each event carries its own participant records.
	 */
	get Rows(): AvailabilityRow[]{
		const byPerson = new Map<string, AvailabilityRow>();
		for(const column of this.Columns){
			for(const participant of (column.event.participants || [])){
				const key = this.personKey(participant);
				if(byPerson.has(key)) continue;
				byPerson.set(key, {
					key,
					name: participant.name || 'Unknown',
					// Only a real athlete/coach profile id may go to the profile lookup. The
					// participant id is a roster entry id, and looking that up returns somebody
					// else's profile, whose name then replaces this person's on the chip.
					profileId: participant.coachId || participant.athleteId || null,
					isStaff: !!participant.coachId,
					userInfo: this.participantToUserInfo(participant),
				});
			}
		}
		return Array.from(byPerson.values()).sort((a, b) => {
			if(a.isStaff !== b.isStaff) return Number(a.isStaff) - Number(b.isStaff);
			return a.name.localeCompare(b.name);
		});
	}

	get OutstandingLabel(): string{
		const outstanding = this.Columns.reduce((total, column) => total + this.noReplyCount(column), 0);
		const total = this.Columns.length * this.Rows.length;
		if(total === 0) return '';
		if(outstanding === 0) return 'Everyone has replied';
		return `${outstanding} of ${total} responses outstanding`;
	}

	personKey(participant: CalendarEventParticipant): string{
		if(participant.coachId) return `coach:${participant.coachId}`;
		if(participant.athleteId) return `athlete:${participant.athleteId}`;
		return `participant:${participant.id}`;
	}

	/** The participant record for this person on this event, or undefined if they aren't on it. */
	participantFor(row: AvailabilityRow, column: AvailabilityColumn): CalendarEventParticipant | undefined{
		return (column.event.participants || []).find(p => this.personKey(p) === row.key);
	}

	cellKey(row: AvailabilityRow, column: AvailabilityColumn): string{
		return `${column.key}|${row.key}`;
	}

	/** The answer shown in a cell: this session's edit if there is one, else what the server sent. */
	rsvpFor(row: AvailabilityRow, column: AvailabilityColumn): CalendarEventParticipantResponse{
		const edited = this.edits[this.cellKey(row, column)];
		if(edited) return edited;
		const participant = this.participantFor(row, column);
		if(!participant) return CalendarEventParticipantResponse.None;
		return participantRsvpOn(participant, column.occurrenceDate);
	}

	countOf(column: AvailabilityColumn, response: string): number{
		return this.Rows.filter(row => this.rsvpFor(row, column) === response).length;
	}
	goingCount(column: AvailabilityColumn): number{
		return this.countOf(column, CalendarEventParticipantResponse.Yes);
	}
	noReplyCount(column: AvailabilityColumn): number{
		return this.Rows.filter(row => {
			const answer = this.rsvpFor(row, column);
			return !answer || answer === CalendarEventParticipantResponse.None;
		}).length;
	}

	responseLabel(response: CalendarEventParticipantResponse): string{
		const found = this.Legend.find(item => item.value === response);
		return found ? found.label : 'No reply';
	}
	stateIcon(response: CalendarEventParticipantResponse): string{
		switch(response){
			case CalendarEventParticipantResponse.Yes: return this.BAIcons.checkmark;
			case CalendarEventParticipantResponse.Maybe: return this.BAIcons.checkMaybe;
			case CalendarEventParticipantResponse.No: return this.BAIcons.close;
			default: return 'mdi-minus';
		}
	}
	stateClass(response: CalendarEventParticipantResponse): string{
		switch(response){
			case CalendarEventParticipantResponse.Yes: return 'state-yes';
			case CalendarEventParticipantResponse.Maybe: return 'state-maybe';
			case CalendarEventParticipantResponse.No: return 'state-no';
			default: return 'state-none';
		}
	}
	cellTooltip(row: AvailabilityRow, column: AvailabilityColumn): string{
		const participant = this.participantFor(row, column);
		if(!participant) return `${row.name} is not on this event`;
		const note = participantNoteOn(participant, column.occurrenceDate);
		const answer = `${row.name}: ${this.responseLabel(this.rsvpFor(row, column))}`;
		return note ? `${answer} — “${note}”` : answer;
	}

	/** Click steps through the four answers, which is the fastest way to work down a column. */
	async cycle(row: AvailabilityRow, column: AvailabilityColumn): Promise<void>{
		if(!this.canEdit) return;
		const participant = this.participantFor(row, column);
		if(!participant) return;
		const order = [
			CalendarEventParticipantResponse.Yes,
			CalendarEventParticipantResponse.Maybe,
			CalendarEventParticipantResponse.No,
			CalendarEventParticipantResponse.None,
		];
		const current = this.rsvpFor(row, column);
		const next = order[(order.indexOf(current) + 1) % order.length];
		const key = this.cellKey(row, column);
		const previous = this.edits[key];

		this.error = '';
		this.$set(this.edits, key, next);
		this.savingKey = key;
		try{
			const updated = await this.calendarEventsApi.setParticipantRsvp(
				column.event.id,
				participant.id,
				next,
				undefined,
				column.occurrenceDate,
			);
			this.$emit('updated', updated);
		}catch(e){
			// Put the cell back to what the server still believes, and say so.
			if(previous === undefined) this.$delete(this.edits, key);
			else this.$set(this.edits, key, previous);
			this.error = `Couldn't save ${row.name}'s answer. Try again.`;
			// eslint-disable-next-line no-console
			console.error('Failed to save availability', e);
		}finally{
			this.savingKey = null;
		}
	}

	async remind(column: AvailabilityColumn): Promise<void>{
		this.error = '';
		this.remindingKey = column.key;
		try{
			const result = await this.calendarEventsApi.remindNonResponders(column.event.id, column.occurrenceDate);
			this.$emit('reminded', result);
		}catch(e){
			this.error = 'Could not send reminders. Try again.';
			// eslint-disable-next-line no-console
			console.error('Failed to remind non-responders', e);
		}finally{
			this.remindingKey = null;
		}
	}

	/** One row per person, one column per event date, so it opens straight into a spreadsheet. */
	exportCsv(): void{
		const header = ['Player', ...this.Columns.map(column => `"${column.event.EventName} ${column.dateLabel}"`)];
		const lines = this.Rows.map(row => [
			`"${row.name}"`,
			...this.Columns.map(column => this.responseLabel(this.rsvpFor(row, column))),
		].join(','));
		const csv = [header.join(','), ...lines].join('\n');
		const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
		const link = document.createElement('a');
		link.href = url;
		link.download = `availability-${new Date().toISOString().slice(0, 10)}.csv`;
		link.click();
		URL.revokeObjectURL(url);
	}

	eventColor(eventType: TeamEventType): string{
		return getEventColor(eventType);
	}
	eventTypeLabel(eventType: TeamEventType): string{
		const found = getDefaultEventTypes().find(type => type.name === eventType);
		return found ? found.label : 'Event';
	}
	formatDate(date: Date): string{
		return date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
	}

	participantToUserInfo(participant: CalendarEventParticipant): Partial<UserProfileModel>{
		if(!this.team) return this.nameToUserInfo(participant.name);
		if(participant.coachId){
			const staff = (this.team.staffMembers || []).find(s => s.id === participant.id ||
				(!!participant.coachId && s.coachId === participant.coachId));
			if(staff) return { firstName: staff.firstName, lastName: staff.lastName };
		}else{
			// A null athleteId must not match another null athleteId, or unlinked players borrow
			// the first unlinked roster entry's name.
			const player = (this.team.players || []).find(p => p.id === participant.id ||
				(!!participant.athleteId && p.athleteId === participant.athleteId));
			if(player) return { firstName: player.firstName, lastName: player.lastName };
		}
		return this.nameToUserInfo(participant.name);
	}
	private nameToUserInfo(name: string): Partial<UserProfileModel>{
		const parts = (name || '').split(' ').filter(Boolean);
		return { firstName: parts[0] || 'Unknown', lastName: parts.slice(1).join(' ') };
	}
}
